'use client';

import type { Collection } from '@/app/actions/collection';
import type { ProjectTagType } from '~/model/ProjectTag';
import type { ProjectType } from '~/model/ProjectType';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import { SkeletonItem } from '@/app/[locale]/(home)/components/FollowedProjects/FollowedProjectsSkeleton';
import { MoreProjectsButton } from '@/app/[locale]/(home)/components/MoreProjects/MoreProjectsButton';
import { ProjectItemContextEnum } from '@/app/[locale]/(home)/components/ProjectsScroller/model';
import { ProjectsListItem } from '@/app/[locale]/projects/components';
import { fetchCrossSellProject } from '@/app/actions/server';
import { MainColumn } from '@/app/components/MainColumn';
import { useApiErrorContext } from '@/app/components/Provider';
import { AnalyticEvents } from '~/const/appConst';
import { useRequestState } from '~/hooks/request/useRequestState';

export const CrossSell = ({ projectId }: { projectId: number }) => {
  const t = useTranslations('INTERACTION.CROSS_SELL');

  const { setError } = useApiErrorContext();

  const { startRequest, handleError, handleSuccess } = useRequestState();

  const [projects, setProjects] = useState<Collection<ProjectType>>();
  const [tags, setTags] = useState<Collection<ProjectTagType>>();

  const fetchProjects = useCallback(async () => {
    startRequest();
    const { projects, tags } = await fetchCrossSellProject(projectId);

    if (projects['hydra:description']) {
      handleError();
      return setError(projects);
    }

    setProjects(projects);
    setTags(tags);
    handleSuccess();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return (
    <section className="all-projects" data-testid="cross-sell">
      <MainColumn>
        <h2 className="vp-title-m">{t('TITLE')}</h2>
        <div className="all-projects__inner">
          <ul
            className="projects-list"
            {...projects && { 'data-cy': 'cross-sell' }}
            data-testid="projects-list"
          >
            {!projects && [...Array.from({ length: 4 }).keys()].map(key => <SkeletonItem key={key} />)}
            {projects?.['hydra:member']?.map((project) => {
              return (
                <li key={project['@id']}>
                  <ProjectsListItem
                    project={project}
                    instanceName={ProjectItemContextEnum.cross_sell}
                    imageSizes="(max-width: 768px) 50vw, (max-width: 992px) 25vw, 15vw"
                    tags={tags['hydra:member']}
                  />
                </li>
              );
            })}
          </ul>
          <MoreProjectsButton
            buttonLabel={t('BUTTON')}
            analyticsEvent={AnalyticEvents.CROSS_SELL}
          />
        </div>
      </MainColumn>
    </section>
  );
};
